import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { Logo } from '@/components/atoms/Logo';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});
type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword(data.email);
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" height={44} />
      </Box>

      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Forgot your password?
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3.5}>
        Enter your email and we'll send you a reset link.
      </Typography>

      {sent ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          If that email exists in our system, you'll receive a password reset link shortly.
        </Alert>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <TextField
            {...register('email')}
            label="Email address"
            type="email"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2.5 }}
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? 'Sending…' : 'Send Reset Link'}
          </Button>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Link
          to="/login"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.875rem', color: 'inherit', opacity: 0.7 }}
        >
          <ArrowBackIcon fontSize="small" /> Back to Sign In
        </Link>
      </Box>
    </Box>
  );
}

